import { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { Icon } from '@/components/Icon';
import { site } from '@/config/site';
import { play, unlock } from '@/lib/sound';
import css from './Boot.module.css';

/** How long we wait for a click before letting the visitor through anyway. */
const IDLE_MS = 6000;
/** The "logging on" beat between the click and the desktop. */
const SIGN_IN_MS = 1200;

interface WelcomeScreenProps {
  onDone: () => void;
}

/**
 * The XP Welcome (logon) screen: two darkened blue fields with a lifted band
 * across the middle third, amber hairlines top and bottom, the prompt on the
 * left of a centre rule and the account tile on the right.
 */
export function WelcomeScreen({ onDone }: WelcomeScreenProps) {
  const [signingIn, setSigningIn] = useState(false);
  const tileRef = useRef<HTMLButtonElement>(null);
  // Guards the sound + transition against a double activation (double click,
  // or Enter held down) without routing side effects through a state updater,
  // which StrictMode would run twice in development.
  const startedRef = useRef(false);

  useEffect(() => {
    tileRef.current?.focus();
  }, []);

  useEffect(() => {
    if (signingIn) return;
    // Nobody clicked. Let them through anyway, but *silently*: with no user
    // gesture on record the browser blocks the startup chime, and a sound that
    // half-plays (or plays late, once some unrelated click unlocks audio) is
    // worse than no sound at all.
    const timer = window.setTimeout(onDone, IDLE_MS);
    return () => window.clearTimeout(timer);
  }, [signingIn, onDone]);

  useEffect(() => {
    if (!signingIn) return;
    const timer = window.setTimeout(onDone, SIGN_IN_MS);
    return () => window.clearTimeout(timer);
  }, [signingIn, onDone]);

  const signIn = useCallback(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    // This click is the user gesture the Web Audio API has been waiting for,
    // which is exactly why the startup chime lives here and nowhere else.
    unlock();
    play('startup');
    setSigningIn(true);
  }, []);

  return (
    <div className={css.welcome}>
      <div className={css.welcomeTop} />
      <div className={css.divider} />

      <div className={css.welcomeBand}>
        <div className={css.bandInner}>
          <div className={css.bandLeft}>
            <img
              className={css.welcomeLogo}
              src="/assets/ui/logo_wide_on_dark.png"
              width={240}
              height={45}
              alt=""
              draggable={false}
            />
            <p className={css.prompt} aria-live="polite">
              {signingIn ? 'Loading your personal settings…' : 'To begin, click your user name'}
            </p>
          </div>

          <div className={css.bandRule} />

          <div className={css.bandRight}>
            <button
              ref={tileRef}
              type="button"
              className={clsx(css.tile, signingIn && css.tileBusy)}
              onClick={signIn}
              aria-label={`Log on as ${site.userName}`}
            >
              <span className={css.tilePic}>
                <Icon src="/assets/ui/user-tile.svg" size={48} />
              </span>
              <span className={css.tileName}>{site.userName}</span>
            </button>
          </div>
        </div>
      </div>

      <div className={css.divider} />

      <div className={css.welcomeBottom}>
        {/* Decorative only — there is nothing to turn off. */}
        <span className={css.powerOff} aria-hidden="true">
          Turn off computer
        </span>
      </div>
    </div>
  );
}
